import React from 'react'
import { Helmet } from 'react-helmet-async'
import styled from 'styled-components'
import { STOCKS, STOCK_ISSUER, type Stock } from 'utils/stocks/catalogue'

/** Pieces both stock pages use, and the structured data a crawler reads. */

export const SITE_ORIGIN = 'https://mprolab.io'

/**
 * A value being read from the chain, shown as a bar rather than a dash.
 *
 * A dash is a statement — it means "there is nothing here" — and using it while
 * a read is still in flight tells a visitor the wrong thing for as long as the
 * read takes, then silently contradicts itself when the number lands. Anything
 * on these pages that comes from Base wears one of these until it has an
 * answer; a dash only ever means the answer was nothing.
 */
export const Skeleton = styled.span<{ $width?: string; $height?: string }>`
  display: inline-block;
  width: ${({ $width }) => $width ?? '64px'};
  height: ${({ $height }) => $height ?? '12px'};
  border-radius: 4px;
  vertical-align: middle;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.gray9} 0%,
    ${({ theme }) => theme.colors.gray8} 50%,
    ${({ theme }) => theme.colors.gray9} 100%
  );
  background-size: 200% 100%;
  animation: mprolabStockShimmer 1.4s ease-in-out infinite;

  @keyframes mprolabStockShimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  /* A shimmer is decoration; someone who asked for less motion keeps the bar. */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background: ${({ theme }) => theme.colors.gray9};
  }
`

export const StockIcon = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gray9};
  flex: 0 0 auto;
`

/**
 * Green above zero, red below, muted at nothing.
 *
 * Note what the callers pass for a premium: **minus** the premium. A pool
 * trading under the oracle is the good news for somebody buying, so the colour
 * follows the reader's interest rather than the arithmetic sign, and the number
 * beside it keeps its own sign so nothing is hidden.
 */
export const Tone = styled.span<{ $value: number | null }>`
  color: ${({ theme, $value }) =>
    $value === null ? theme.colors.gray6 : $value > 0 ? theme.colors.green : $value < 0 ? theme.colors.red : theme.colors.gray3};
`

/**
 * `ItemList` for the board. It is what lets a search engine understand the page
 * as thirteen things rather than one wall of numbers, and it names each company
 * with the ticker people actually search for.
 */
export const StocksJsonLd: React.FC = () => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Tokenized stocks on Base',
    description: `Coinbase tokenized equities traded onchain on Base, issued by ${STOCK_ISSUER.name}.`,
    numberOfItems: STOCKS.length,
    itemListElement: STOCKS.map((stock, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: `${stock.ticker} — ${stock.name}`,
      url: `${SITE_ORIGIN}/stocks/${stock.slug}`
    }))
  }
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  )
}

/**
 * One company, as a financial product with a breadcrumb to the board.
 *
 * No price is written into the structured data. A price in JSON-LD is a claim
 * frozen at build time, and these move every block — saying nothing is more
 * accurate than saying what was true on a Thursday.
 */
export const StockJsonLd: React.FC<{ stock: Stock }> = ({ stock }) => {
  const url = `${SITE_ORIGIN}/stocks/${stock.slug}`
  const data = [
    {
      '@context': 'https://schema.org',
      '@type': 'FinancialProduct',
      name: `${stock.symbol} — tokenized ${stock.ticker} on Base`,
      description: stock.about,
      url,
      category: 'Tokenized equity',
      provider: { '@type': 'Organization', name: STOCK_ISSUER.name }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Tokenized stocks', item: `${SITE_ORIGIN}/stocks` },
        { '@type': 'ListItem', position: 2, name: stock.ticker, item: url }
      ]
    }
  ]
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  )
}
